import { useEffect, useMemo, useState } from 'react';
import {
  useAllBillboards,
  useBillboardCatalog,
  useDynamicVenues,
} from '@/features/billboards/api/useBillboardQueries';
import {
  findLocationTypes,
  findMidOptions,
  getMergedMidOptions,
  getMergedSubOptions,
  getMidLabel,
  getSubLabel,
  toChip,
  type VenueHierarchy,
} from '@/features/billboards/types/hierarchy';
import type { LocalityPlace } from '@/features/billboards/components/list/LocalityRadiusField';
import { NEARBY_DEFAULT_RADIUS } from '@/features/billboards/utils/nearbyBillboardsUtils';
import { matchesBillboardFilters } from '@/features/billboards/utils/billboardsListUtils';
import type { BillboardListParams } from '@/features/billboards/api/billboardApi';
import { useDebounce } from '@/shared/hooks/useDebounce';
import {
  countActiveScreenFilters,
  nextScreenFilters,
  parseScreenList,
  parseScreenTotalPages,
  pruneScreenFilters,
} from './screensStepHelpers';
import {
  EMPTY_SCREEN_FILTERS,
  type ScreenFilters,
  type ScreenOrientationFilter,
  type ScreenSortMode,
  type ScreensStepProps,
} from './ScreensStep.types';
import { normalizeDetectedOrientation } from '../ManualBuilder/orientationAssist';

const EMPTY_LOCALITY_PLACE: LocalityPlace = {
  label: '',
  lat: null,
  lng: null,
  radius: NEARBY_DEFAULT_RADIUS,
};

function screenPrice(screen: { basePrice?: number }) {
  return Number(screen.basePrice) || 0;
}

export function useScreensStep({
  campaignData,
}: Pick<ScreensStepProps, 'campaignData'>) {
  const creativeOrientation = normalizeDetectedOrientation(
    campaignData.mediaIds?.[0]?.orientation,
  );

  const initialOrientationFilter: ScreenOrientationFilter =
    creativeOrientation === 'PORTRAIT' || creativeOrientation === 'LANDSCAPE'
      ? 'MATCHING'
      : 'ALL';

  const [orientationFilter, setOrientationFilterState] =
    useState<ScreenOrientationFilter>(initialOrientationFilter);
  const [sortMode, setSortMode] = useState<ScreenSortMode>('recommended');
  const [search, setSearch] = useState('');
  const [areaQuery, setAreaQuery] = useState('');
  const [localityPlace, setLocalityPlace] =
    useState<LocalityPlace>(EMPTY_LOCALITY_PLACE);
  const [filters, setFilters] = useState<ScreenFilters>(EMPTY_SCREEN_FILTERS);
  const [page, setPage] = useState(1);

  const debouncedSearch = useDebounce(search, 400);
  const debouncedAreaQuery = useDebounce(areaQuery, 400);
  const debouncedLocality = useDebounce(localityPlace.label?.trim() || '', 400);
  const city = campaignData.targetCities.join(',');
  const hasCoords =
    Number.isFinite(localityPlace.lat) && Number.isFinite(localityPlace.lng);
  const hasLocalityQuery = Boolean(debouncedLocality);
  const hasAreaQuery = Boolean(debouncedAreaQuery.trim());
  const hasLocationType = filters.locationType.length > 0;
  const hasVenueType = filters.venueType.length > 0;
  const hasSubType = filters.venueSubType.length > 0;

  const targetOrientation =
    orientationFilter === 'MATCHING'
      ? creativeOrientation !== 'UNKNOWN'
        ? creativeOrientation
        : undefined
      : orientationFilter !== 'ALL'
        ? orientationFilter
        : undefined;

  const hasOrientationFilter = Boolean(targetOrientation);

  const hasClientFiltering =
    hasCoords ||
    hasLocalityQuery ||
    hasAreaQuery ||
    hasLocationType ||
    hasVenueType ||
    hasSubType ||
    hasOrientationFilter;

  const listParams = {
    page,
    limit: 12,
    city,
    search: debouncedSearch || undefined,
    startDate: campaignData.startDate,
    endDate: campaignData.endDate,
    screenOrientation: targetOrientation,
    locationType: filters.locationType.length ? filters.locationType : undefined,
    venueType: filters.venueType.length ? filters.venueType : undefined,
    VenueSubType: filters.venueSubType.length ? filters.venueSubType : undefined,
    ...(hasCoords
      ? {
          lat: localityPlace.lat,
          lng: localityPlace.lng,
          radius: localityPlace.radius || NEARBY_DEFAULT_RADIUS,
          areaFilter: true,
        }
      : {}),
  };

  const venuesQuery = useDynamicVenues({}, city || undefined);
  const hierarchy = venuesQuery.data?.data as VenueHierarchy | undefined;

  const locationTypes = (hierarchy?.locationTypes || []).map(toChip);
  const selectedLocations = findLocationTypes(hierarchy, filters.locationType);
  const mergedMids = getMergedMidOptions(selectedLocations);
  const midOptions = mergedMids.map(toChip);
  const selectedMids = findMidOptions(selectedLocations, filters.venueType);
  const subOptions = getMergedSubOptions(selectedMids).map(toChip);

  const query = useAllBillboards(listParams, !hasClientFiltering);
  // Always load catalog so orientation filter counts stay available,
  // even when the paged list API is used without client-side filtering.
  const catalogQuery = useBillboardCatalog(listParams, true);

  const pagedScreens = useMemo(() => parseScreenList(query.data), [query.data]);
  const billboardFilters = useMemo(
    (): BillboardListParams => ({
      screenOrientation: targetOrientation,
      locationType: hasLocationType ? filters.locationType : undefined,
      venueType: hasVenueType ? filters.venueType : undefined,
      VenueSubType: hasSubType ? filters.venueSubType : undefined,
      area: debouncedAreaQuery.trim() || undefined,
      locality: debouncedLocality || undefined,
      lat: hasCoords ? localityPlace.lat : undefined,
      lng: hasCoords ? localityPlace.lng : undefined,
      radius: hasCoords ? localityPlace.radius || NEARBY_DEFAULT_RADIUS : undefined,
    }),
    [
      targetOrientation,
      hasLocationType,
      filters.locationType,
      hasVenueType,
      filters.venueType,
      hasSubType,
      filters.venueSubType,
      debouncedAreaQuery,
      debouncedLocality,
      hasCoords,
      localityPlace.lat,
      localityPlace.lng,
      localityPlace.radius,
    ],
  );

  const filteredScreens = useMemo(() => {
    if (!hasClientFiltering) return [];

    return (catalogQuery.data?.billboards ?? []).filter((item) =>
      matchesBillboardFilters(item, billboardFilters),
    );
  }, [catalogQuery.data?.billboards, hasClientFiltering, billboardFilters]);

  // Orientation counts across the catalog for quick tabs
  const orientationCounts = useMemo(() => {
    const all = catalogQuery.data?.billboards ?? [];
    let matching = 0;
    let portrait = 0;
    let landscape = 0;

    for (const b of all) {
      const o = String(b.screenOrientation || '').toUpperCase();
      if (o === 'PORTRAIT') portrait++;
      if (o === 'LANDSCAPE') landscape++;
      if (
        creativeOrientation &&
        creativeOrientation !== 'UNKNOWN' &&
        o === creativeOrientation
      ) {
        matching++;
      }
    }

    return {
      total: all.length,
      matching,
      portrait,
      landscape,
    };
  }, [catalogQuery.data?.billboards, creativeOrientation]);

  const catalogAll = catalogQuery.data?.billboards ?? [];
  const useCatalogPaging = hasClientFiltering || sortMode !== 'recommended';

  // Sort + paginate. Price sorts use catalog so ordering isn't limited to one API page.
  const screens = useMemo(() => {
    const source = useCatalogPaging
      ? hasClientFiltering
        ? filteredScreens
        : catalogAll
      : pagedScreens;

    let ordered = [...source];

    if (sortMode === 'price_asc') {
      ordered.sort((a, b) => screenPrice(a) - screenPrice(b));
    } else if (sortMode === 'price_desc') {
      ordered.sort((a, b) => screenPrice(b) - screenPrice(a));
    } else if (
      orientationFilter === 'ALL' &&
      creativeOrientation &&
      creativeOrientation !== 'UNKNOWN'
    ) {
      ordered.sort((a, b) => {
        const aMatch =
          String(a.screenOrientation || '').toUpperCase() ===
          creativeOrientation;
        const bMatch =
          String(b.screenOrientation || '').toUpperCase() ===
          creativeOrientation;
        if (aMatch && !bMatch) return -1;
        if (!aMatch && bMatch) return 1;
        return 0;
      });
    }

    if (useCatalogPaging) {
      return ordered.slice((page - 1) * 12, page * 12);
    }
    return ordered;
  }, [
    useCatalogPaging,
    hasClientFiltering,
    filteredScreens,
    catalogAll,
    pagedScreens,
    page,
    orientationFilter,
    creativeOrientation,
    sortMode,
  ]);

  const totalPages = useCatalogPaging
    ? Math.max(
        1,
        Math.ceil(
          (hasClientFiltering ? filteredScreens.length : catalogAll.length) /
            12,
        ) || 1,
      )
    : parseScreenTotalPages(query.data);

  const listQuery = useCatalogPaging ? catalogQuery : query;

  const activeFilterCount = useMemo(
    () =>
      countActiveScreenFilters(
        filters,
        hasCoords || hasLocalityQuery,
        hasAreaQuery,
      ),
    [filters, hasCoords, hasLocalityQuery, hasAreaQuery],
  );


  useEffect(() => {
    if (!listQuery.isPending && filteredScreens.length > 0 && page > totalPages) {
      setPage(totalPages);
    }
  }, [listQuery.isPending, filteredScreens.length, totalPages, page]);

  const updateFilter = (key: keyof ScreenFilters, value: string[]) => {
    setFilters((prev) => {
      const next = nextScreenFilters(prev, key, value);
      const locations = findLocationTypes(hierarchy, next.locationType);
      const mids = getMergedMidOptions(locations);
      const selectedMids = findMidOptions(locations, next.venueType);
      const subs = getMergedSubOptions(selectedMids);
      return pruneScreenFilters(
        next,
        mids.flatMap((item) => [item.key, item.label]),
        subs.flatMap((item) => [item.key, item.label]),
      );
    });
    setPage(1);
  };

  const clearFilters = () => {
    setFilters(EMPTY_SCREEN_FILTERS);
    setLocalityPlace(EMPTY_LOCALITY_PLACE);
    setAreaQuery('');
    setPage(1);
  };

  const applyLocationFilters = (draft: {
    localityPlace: LocalityPlace;
    areaQuery: string;
    filters: ScreenFilters;
  }) => {
    setLocalityPlace(draft.localityPlace);
    setAreaQuery(draft.areaQuery);
    setFilters(draft.filters);
    setPage(1);
  };

  return {
    search,
    areaQuery,
    localityPlace,
    filters,
    orientationFilter,
    sortMode,
    setSortMode: (mode: ScreenSortMode) => {
      setSortMode(mode);
      setPage(1);
    },
    setOrientationFilter: (filter: ScreenOrientationFilter) => {
      setOrientationFilterState(filter);
      setPage(1);
    },
    orientationCounts,
    creativeOrientation,
    page,
    setPage,
    setSearchValue: (value: string) => {
      setSearch(value);
      setPage(1);
    },
    setAreaQueryValue: (value: string) => {
      setAreaQuery(value);
      setPage(1);
    },
    setLocalityPlaceValue: (value: LocalityPlace) => {
      setLocalityPlace(value);
      setPage(1);
    },
    applyLocationFilters,
    city,
    venuesQuery,
    hierarchy,
    locationTypes,
    midOptions,
    subOptions,
    midLabel: getMidLabel(selectedLocations[0]),
    subLabel: getSubLabel(selectedMids[0]),
    query: listQuery,
    screens,
    totalPages,
    activeFilterCount,
    updateFilter,
    clearFilters,
  };
}
